import { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import documentEditorServiceApi from 'shared/api/document-editor.service.api';
import { DOCUMENT_EDITOR_MODES } from 'shared/constants/document-editor.constants';
import { getLang } from 'shared/store/selectors/lang.selector';
import { getFileNameFromUrl } from 'shared/utils';
import Logger from 'shared/utils/Logger';
import { useDocumentEditor } from './use-document-editor';
import { resolveConfigSource } from './resolve-config-source';

const logger = Logger.create({ loggerName: 'DocumentEditorConfig' });

/**
 * Hook to fetch Document Editor configuration from the backend.
 * Saved items are fetched by item id; unsaved items (post-upload, pre-save)
 * fall back to a config fetched by the asset file name from the link.
 *
 * @param {{ itemId?: string, link?: string }} source - Knowledge item ID and/or uploaded file link
 * @param {'view' | 'edit'} mode - 'view' for read-only, 'edit' for editing
 * @returns {{ config: object | null, loading: boolean, error: Error | null, refetch: () => Promise<void> }}
 */
export function useDocumentEditorConfig({ itemId, link }, mode = DOCUMENT_EDITOR_MODES.VIEW) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const lang = useSelector(getLang('DOCUMENT_EDITOR'));
  const { isSupported } = useDocumentEditor();

  const fetchConfig = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const isStale = () => requestId !== requestIdRef.current;
    const assetFileName = getFileNameFromUrl(link);
    const source = resolveConfigSource({ itemId, assetFileName });

    if (!source) {
      logger.error('Editor config requested without item id or file link', { link });
      setError(new Error(lang.CONFIG_ERROR));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response =
        source.kind === 'item'
          ? await documentEditorServiceApi.getEditorConfig(source.itemId, mode)
          : await documentEditorServiceApi.getAssetEditorConfig(source.assetFileName, mode);

      if (isStale()) return;

      if (!response.success || !response.data?.config) {
        logger.error('Failed to load editor config', {
          itemId,
          assetFileName,
          mode,
          code: response.code,
        });
        setError(new Error(lang.CONFIG_ERROR));
        setConfig(null);
        return;
      }

      setConfig(response.data.config);
    } catch (err) {
      if (isStale()) return;
      logger.error('Unexpected error loading editor config', {
        itemId,
        assetFileName,
        error: err instanceof Error ? err.message : String(err),
      });
      setError(new Error(lang.CONFIG_ERROR));
      setConfig(null);
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  }, [itemId, link, mode, lang]);

  useEffect(() => {
    if (isSupported) {
      fetchConfig();
    }
  }, [fetchConfig, isSupported]);

  // Invalidate in-flight requests on unmount so they can't set state afterwards
  useEffect(
    () => () => {
      requestIdRef.current++;
    },
    [],
  );

  return {
    config,
    loading,
    error,
    refetch: fetchConfig,
  };
}
